"use client"

import styled from "@emotion/styled"
import { Button } from "@mui/material"
import Image from "next/image"
import { Desktop, DownloadIconNew, Mobile, NewText } from "ui"
import {
  extractProperty,
  extractPropertyMediaItems,
  getNonUmbracoUrl,
  getUmbracoMediaUrl,
  getUmbracoPDFUrl,
} from "utils"

const StyledAdditionalDetailsWrapper = styled.div`
  width: 100%;
  padding: 32px 0;
  border-bottom: 1px solid #dedede;
  border-top: 1px solid #dedede;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const StyledAdditionalDetailsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;

  li {
    position: relative;
    padding-left: 16px;

    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 6px;
      height: 5px;
      background: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" width="6" height="5" viewBox="0 0 6 5" fill="none"><circle cx="3" cy="2.5" r="2.5" fill="%23009919"/></svg>')
        no-repeat center;
    }
  }
`

const StyledStandardsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  width: 100%;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 12px;
  }
`

const StyledStandardButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border-radius: 50%;
  background-color: transparent;
  box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.08);
  min-width: 0;
  width: 40px;
  height: 40px;
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 50px;
    height: 50px;
  }
`

const StyledTechnicalSpecsButton = styled(Button)`
  background-color: ${(p) => p.theme.colors.buttonActiveBackgroundColor};
  color: ${(p) => p.theme.colors.buttonHoverTextColor};
  width: fit-content;
  padding: 8px 16px;
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.16);
  border-radius: 200px;
  font-size: 18px;
  font-weight: 600;
  line-height: normal;
  gap: 6px;
  text-decoration: none;
  :hover {
    background-color: ${(p) => p.theme.colors.buttonHoverBackgroundColor};
    text-decoration: none;
  }
`

const AdditionalDetailsBlock = ({ data }: { data: any }) => {
  const productAdditionalDetailsList: { [key: string]: string }[] = extractProperty(
    data,
    "additionalDetails",
  )?.map((additionalDetail: any) => {
    return {
      key: extractProperty(additionalDetail, "title"),
      value: extractProperty(additionalDetail, "text"),
    }
  })

  const standards = extractProperty(data, "standards")?.map((standard: any) => {
    return {
      icon: extractPropertyMediaItems(standard, "icon")?.[0],
      standardsFile: extractPropertyMediaItems(standard, "standardsFile")?.[0],
    }
  })

  const technicalSpecs: { name: string; target: string; url: string } = extractProperty(
    data,
    "technicalSpec",
  )?.[0]

  const technicalSpecsUrl = technicalSpecs?.url?.startsWith("/media/")
    ? getUmbracoPDFUrl(technicalSpecs?.url)
    : technicalSpecs?.url?.includes("pages")
    ? `${window.location?.origin}${getNonUmbracoUrl(technicalSpecs?.url)}`
    : technicalSpecs?.url

  if (!productAdditionalDetailsList?.length && !standards?.length && !technicalSpecsUrl) {
    return null
  }

  return (
    <StyledAdditionalDetailsWrapper>
      {productAdditionalDetailsList?.length > 0 && (
        <StyledAdditionalDetailsList>
          {productAdditionalDetailsList
            ?.filter((detail) => detail.value)
            .map((detail, index) => {
              return (
                <li key={index}>
                  <NewText
                    component="p"
                    desktopFontSize={20}
                    desktopFontWeight={400}
                    desktopLineHeight={"100%"}
                    mobileFontSize={18}
                    mobileFontWeight={400}
                    mobileLineHeight={"100%"}
                    color="black"
                  >
                    {detail.key && <strong>{detail.key} : </strong>} {detail?.value}
                  </NewText>
                </li>
              )
            })}
        </StyledAdditionalDetailsList>
      )}
      {standards?.length > 0 && (
        <StyledStandardsWrapper>
          {standards.map((standard: { icon: string; standardsFile: string }, index: number) => {
            return (
              <StyledStandardButton
                key={index}
                rel="noopener noreferrer"
                onClick={() =>
                  standard.standardsFile &&
                  window.open(getUmbracoMediaUrl(standard.standardsFile), "_blank")
                }
                style={{ cursor: standard.standardsFile ? "pointer" : "default" }}
              >
                {standard.icon && (
                  <>
                    <Mobile>
                      <Image
                        src={getUmbracoMediaUrl(standard.icon)}
                        alt={`Standard Icon ${index + 1}`}
                        width={40}
                        height={40}
                        style={{ objectFit: "contain" }}
                      />
                    </Mobile>
                    <Desktop>
                      <Image
                        src={getUmbracoMediaUrl(standard.icon)}
                        alt={`Standard Icon ${index + 1}`}
                        width={50}
                        height={50}
                        style={{ objectFit: "contain" }}
                      />
                    </Desktop>
                  </>
                )}
              </StyledStandardButton>
            )
          })}
        </StyledStandardsWrapper>
      )}
      {technicalSpecs?.url && (
        <StyledTechnicalSpecsButton
          onClick={() => window.open(getUmbracoMediaUrl(technicalSpecsUrl), "_blank")}
        >
          {technicalSpecs?.name}
          <DownloadIconNew />
        </StyledTechnicalSpecsButton>
      )}
    </StyledAdditionalDetailsWrapper>
  )
}

export default AdditionalDetailsBlock
